import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { api } from '../api.js';
import { useAuth } from '../auth.jsx';
import Field from '../components/Field.jsx';
import Alert from '../components/ui/Alert.jsx';
import CopyButton from '../components/ui/CopyButton.jsx';
import { AskColleagueModal } from './company/DomainVerify.jsx';
import { CodeBlock } from './company/Website.jsx';
import { STEPS, loadSetupData, setupStatus } from './setupSteps.js';

// The first-run guided setup: distraction free, one thing at a time, every step skippable.
// Shown straight after "Your company" and whenever an owner/admin lands home before
// finishing it (organisation.setup_done). Everything it does uses the same API as the
// Company, People, Suppliers and Website pages, so nothing here is set up differently.

const STORE = (orgId) => `ss_setup_step_${orgId}`;

// Line icons for the guide (no emoji)
const ICON = {
  wave: <><path d="M7 11V6a1.5 1.5 0 0 1 3 0v5" /><path d="M10 10V4.5a1.5 1.5 0 0 1 3 0V10" /><path d="M13 10V5.5a1.5 1.5 0 0 1 3 0V12" /><path d="M16 9.5a1.5 1.5 0 0 1 3 0V14a7 7 0 0 1-12.6 4.2L4 15a1.6 1.6 0 0 1 2.4-2.1L7 13.5" /></>,
  star: <path d="M12 3l2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.9 1-6.1-4.4-4.3 6.1-.9z" />,
  lock: <><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></>,
};
const Icon = ({ name, size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{ICON[name]}</svg>
);


function dnsWords(r) {
  const s = String(r || '');
  if (s === 'found') return 'Found it.';
  if (/^not found|^no TXT/i.test(s)) return 'We looked, but the record isn’t there yet.';
  if (/^lookup failed/i.test(s)) return 'We couldn’t reach your domain’s settings just then.';
  return s;
}

function Tick({ done }) {
  return <span className={`setup-tick${done ? ' is-done' : ''}`} aria-hidden="true">{done ? '✓' : ''}</span>;
}

function StepFooter({ onNext, onBack, done, nextLabel }) {
  return (
    <div className="setup-footer">
      {onBack ? <button type="button" className="btn btn-secondary" onClick={onBack}>Back</button> : <span />}
      <div className="setup-footer-right">
        {!done && <button type="button" className="link-button" onClick={onNext}>Skip this step</button>}
        <button type="button" className="btn btn-primary" onClick={onNext}>{nextLabel || (done ? 'Continue' : 'Continue')}</button>
      </div>
    </div>
  );
}

/** A small "added" list under a form. */
function AddedList({ items, empty }) {
  if (!items.length) return empty ? <p className="muted small">{empty}</p> : null;
  return (
    <ul className="setup-added">
      {items.map((i) => <li key={i.key}><Tick done /><span><strong>{i.title}</strong>{i.sub && <span className="muted small"> {i.sub}</span>}</span></li>)}
    </ul>
  );
}

// ---- 1. Domain --------------------------------------------------------------------

function DomainStep({ org, summary, onChanged, footer, goWebsite }) {
  const domains = summary.domains || [];
  const [domainId, setDomainId] = useState(() => (domains.find((d) => d.status !== 'verified') || domains[0])?.id || null);
  const [d, setD] = useState(null);
  const [checking, setChecking] = useState(false);
  const [auto, setAuto] = useState(false);
  const [notice, setNotice] = useState(null);
  const [asking, setAsking] = useState(false);
  const [way, setWay] = useState('dns');
  const base = `/portal/organisations/${org.id}/domains/${domainId}`;

  const load = useCallback(() => { if (domainId) api(base).then((r) => setD(r.domain)).catch(() => setD(null)); }, [base, domainId]);
  useEffect(() => { setD(null); setNotice(null); setAuto(false); load(); }, [load]);

  const check = useCallback(async (quiet = false) => {
    setChecking(true); if (!quiet) setNotice(null);
    try {
      const res = await api(`${base}/check-dns`, { method: 'POST' });
      setD(res.domain);
      if (res.conflict) setNotice({ tone: 'danger', text: 'Another company has already proved this domain is theirs. If it’s yours, contact us.' });
      else if (res.found) { setNotice({ tone: 'success', text: `Found it. ${res.domain.domain} is verified.` }); setAuto(false); onChanged(); }
      else if (!quiet) setNotice({ tone: 'warn', text: `${dnsWords(res.result)} New records can take a few minutes to appear, sometimes up to an hour.` });
    } catch (err) { if (!quiet) setNotice({ tone: 'danger', text: err.message }); }
    setChecking(false);
  }, [base, onChanged]);

  // "I've added it": look again every 30 seconds for up to 15 minutes
  useEffect(() => {
    if (!auto) return undefined;
    let n = 0;
    const t = setInterval(() => { n += 1; if (n > 30) { setAuto(false); return; } check(true); }, 30000);
    return () => clearInterval(t);
  }, [auto, check]);

  if (!domains.length) {
    return (
      <>
        <p className="setup-lede">You haven’t added a domain yet. Add the one your company sends email from on your Company page, then come back to prove it.</p>
        {footer}
      </>
    );
  }
  const verified = d?.status === 'verified';
  return (
    <>
      <p className="setup-lede">This is the most important step. Proving your domain is yours lets us tell people that an email from it really is you, and stops anyone else claiming it.</p>
      {domains.length > 1 && (
        <div className="setup-chips" role="group" aria-label="Domain">
          {domains.map((x) => (
            <button key={x.id} type="button" className={`setup-chip${x.id === domainId ? ' is-on' : ''}`} onClick={() => setDomainId(x.id)}>
              <Tick done={x.status === 'verified'} />{x.domain}
            </button>
          ))}
        </div>
      )}
      {notice && <Alert tone={notice.tone}>{notice.text}</Alert>}
      {!d ? <p className="muted">Loading…</p> : verified ? (
        <div className="setup-success">
          <span className="setup-success-icon" aria-hidden="true">✓</span>
          <div><h3>{d.domain} is verified</h3><p className="muted">Emails from {d.domain} can now be confirmed as yours, and nobody else can claim it.</p></div>
        </div>
      ) : (
        <>
          <div className="setup-ways" role="tablist" aria-label="How to prove it">
            {[['dns', 'Add a DNS record', 'Recommended. Two minutes for whoever runs your domain.'], ['site', 'Add our website code', 'If you can edit your website.'], ['email', 'Ask a colleague', 'No technical changes. Needs two people.']].map(([k, t, s]) => (
              <button key={k} type="button" role="tab" aria-selected={way === k} className={`setup-way${way === k ? ' is-on' : ''}`} onClick={() => setWay(k)}>
                <strong>{t}</strong><span>{s}</span>
              </button>
            ))}
          </div>
          {way === 'dns' && (
            <div className="setup-panel">
              <p>Add this record wherever your domain is managed (GoDaddy, Cloudflare, 123-reg, your IT team…). Copy each value exactly.</p>
              <div className="setup-record">
                <div><span className="setup-record-k">Type</span><span className="mono">TXT</span><CopyButton text="TXT" /></div>
                <div><span className="setup-record-k">Name / host</span><span className="mono">@</span><CopyButton text="@" /></div>
                <div><span className="setup-record-k">Value</span><span className="mono setup-record-v">{d.dns.value}</span><CopyButton text={d.dns.value} /></div>
              </div>
              <div className="setup-check-row">
                <button type="button" className="btn btn-primary" onClick={() => check(false)} disabled={checking}>{checking ? 'Checking…' : 'Check now'}</button>
                {!auto
                  ? <button type="button" className="btn btn-secondary" onClick={() => { setAuto(true); check(true); }}>I’ve added it, keep checking</button>
                  : <span className="setup-watching"><span className="setup-pulse" aria-hidden="true" />Watching for your record. We look every 30 seconds, so you can carry on with the next step.</span>}
              </div>
              {d.dns.last_checked_at && !notice && <p className="muted small">Last checked: {dnsWords(d.dns.last_result)}</p>}
            </div>
          )}
          {way === 'site' && (
            <div className="setup-panel">
              <p>Adding our code to your website proves the domain on its own, and protects your site against copies. We’ll do it together in the website step.</p>
              <button type="button" className="btn btn-primary" onClick={goWebsite}>Go to the website step</button>
            </div>
          )}
          {way === 'email' && (
            <div className="setup-panel">
              <p>We email a colleague at an address on {d.domain} (like info@ or admin@) and ask them to confirm it belongs to your company. Together with someone on your team signing in with an @{d.domain} address (the next step), that proves it.</p>
              <button type="button" className="btn btn-primary" onClick={() => setAsking(true)}>Ask a colleague to approve</button>
              {d.requests?.length > 0 && <p className="muted small">Sent to {d.requests[0].email}: {d.requests[0].status}.</p>}
            </div>
          )}
        </>
      )}
      {footer}
      {asking && d && (
        <AskColleagueModal base={base} domain={d} onClose={() => setAsking(false)}
          onSent={(res) => { setAsking(false); setD(res.domain); setNotice({ tone: 'success', text: `Email sent to ${res.email}. The link works for 24 hours.` }); }} />
      )}
    </>
  );
}

// ---- 2. Business email --------------------------------------------------------------

function EmailStep({ summary, footer }) {
  const { user, refresh } = useAuth();
  const domains = (summary.domains || []).map((d) => d.domain);
  const mine = String(user?.email || '').split('@')[1];
  const onDomain = domains.includes(mine);
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(null);
  const [code, setCode] = useState('');
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const send = async (e) => {
    e?.preventDefault(); setBusy(true); setErrors({}); setError('');
    try { const r = await api('/portal/account/business-email', { method: 'POST', body: { email } }); setSent(r.email); }
    catch (err) { if (err.data?.fields) setErrors(err.data.fields); else setError(err.message); }
    setBusy(false);
  };
  const verify = async (e) => {
    e?.preventDefault(); setBusy(true); setErrors({}); setError('');
    try { await api('/portal/account/business-email/verify', { method: 'POST', body: { code } }); setDone(true); await refresh(); }
    catch (err) { if (err.data?.fields) setErrors(err.data.fields); else setError(err.message); }
    setBusy(false);
  };

  if (onDomain || done) {
    return (
      <>
        <div className="setup-success">
          <span className="setup-success-icon" aria-hidden="true">✓</span>
          <div><h3>You’re signed in with your business email</h3><p className="muted">{user.email} is on your company’s domain, so it counts as someone on your team at {mine}. You’ll use it to sign in from now on.</p></div>
        </div>
        {footer}
      </>
    );
  }
  return (
    <>
      <p className="setup-lede">You signed up with <strong>{user.email}</strong>. If you have a work address on your company’s domain, confirm it here and it becomes your sign-in. It also helps prove your domain.</p>
      {error && <Alert tone="danger">{error}</Alert>}
      {!sent ? (
        <form className="setup-form" onSubmit={send}>
          <Field label="Your work email address" type="email" value={email} onChange={(e) => { setEmail(e.target.value); setErrors({}); }}
            error={errors.email} placeholder={domains[0] ? `you@${domains[0]}` : 'you@yourcompany.com'} autoComplete="email" autoCapitalize="none" />
          <button type="submit" className="btn btn-primary" disabled={busy || !email.includes('@')}>{busy ? 'Sending…' : 'Send me a code'}</button>
        </form>
      ) : (
        <form className="setup-form" onSubmit={verify}>
          <Alert tone="success">We’ve sent a 6-digit code to {sent}. It works for 15 minutes.</Alert>
          <Field label="The code from the email" value={code} onChange={(e) => { setCode(e.target.value.replace(/[^\d ]/g, '')); setErrors({}); }}
            error={errors.code} inputMode="numeric" autoComplete="one-time-code" maxLength={7} className="setup-code" />
          <div className="setup-inline">
            <button type="submit" className="btn btn-primary" disabled={busy || code.replace(/\s/g, '').length !== 6}>{busy ? 'Checking…' : 'Confirm'}</button>
            <button type="button" className="link-button" onClick={() => { setSent(null); setCode(''); }}>Use a different address</button>
          </div>
        </form>
      )}
      {footer}
    </>
  );
}

// ---- 3. People and sending addresses ------------------------------------------------

const BLANK_PERSON = { first_name: '', last_name: '', job_title: '', email: '', kind: 'employee' };

function PeopleStep({ org, people, identifiers, onChanged, footer }) {
  const [p, setP] = useState(BLANK_PERSON);
  const [addr, setAddr] = useState({ value: '', label: '' });
  const [errors, setErrors] = useState({});
  const [aErrors, setAErrors] = useState({});
  const [busy, setBusy] = useState('');
  const [notice, setNotice] = useState('');
  const set = (k) => (e) => { setP({ ...p, [k]: e.target.value }); setErrors({}); };

  const addPerson = async (e) => {
    e.preventDefault(); setBusy('person'); setErrors({}); setNotice('');
    try {
      const r = await api(`/portal/organisations/${org.id}/people`, { method: 'POST', body: p });
      setNotice(r.confirmation === 'sent' ? `${p.first_name} added. We’ve emailed ${p.email} to confirm the address is theirs.` : `${p.first_name} added.`);
      setP(BLANK_PERSON); onChanged();
    } catch (err) { setErrors(err.data?.fields || { first_name: err.message }); }
    setBusy('');
  };
  const addAddress = async (e) => {
    e.preventDefault(); setBusy('addr'); setAErrors({});
    try {
      await api(`/portal/organisations/${org.id}/entities/${org.entity_id}/identifiers`, { method: 'POST', body: { type: 'email_address', value: addr.value, label: addr.label || 'Sends for us', visibility: 'confirm_only' } });
      setAddr({ value: '', label: '' }); onChanged();
    } catch (err) { setAErrors({ value: err.data?.fields?.value || err.message }); }
    setBusy('');
  };
  const addresses = identifiers.filter((i) => i.type === 'email_address');
  return (
    <>
      <p className="setup-lede">Scammers borrow real names. Tell us who emails your exhibitors and attendees, and any other addresses that send for you, so their genuine emails are confirmed and anyone pretending to be them stands out.</p>
      {notice && <Alert tone="success">{notice}</Alert>}
      <div className="setup-two">
        <form className="setup-card" onSubmit={addPerson}>
          <h3>Add a person</h3>
          <div className="setup-grid-2">
            <Field label="First name" value={p.first_name} onChange={set('first_name')} error={errors.first_name} autoComplete="off" />
            <Field label="Last name" value={p.last_name} onChange={set('last_name')} error={errors.last_name} autoComplete="off" />
          </div>
          <Field label="Job title" optional value={p.job_title} onChange={set('job_title')} placeholder="Sales manager" />
          <Field label="Their email address" type="email" value={p.email} onChange={set('email')} error={errors.email} autoCapitalize="none" />
          <Field label="How they work with you" as="select" value={p.kind} onChange={set('kind')} error={errors.kind}>
            <option value="employee">Employee</option><option value="freelancer">Freelancer</option><option value="agency">Agency</option>
          </Field>
          <button type="submit" className="btn btn-primary" disabled={busy === 'person'}>{busy === 'person' ? 'Adding…' : 'Add and add another'}</button>
          <AddedList items={people.map((x) => ({ key: x.id, title: [x.first_name, x.last_name].filter(Boolean).join(' ') || x.name, sub: x.job_title || '' }))} empty="Nobody added yet." />
        </form>
        <form className="setup-card" onSubmit={addAddress}>
          <h3>Other addresses that send for you</h3>
          <p className="muted small">For example your newsletter tool, your ticketing system or an agency. Your own domain is covered already.</p>
          <Field label="Email address" type="email" value={addr.value} onChange={(e) => { setAddr({ ...addr, value: e.target.value }); setAErrors({}); }} error={aErrors.value} placeholder="news@mail.yournewsletter.com" autoCapitalize="none" />
          <Field label="What it’s for" optional value={addr.label} onChange={(e) => setAddr({ ...addr, label: e.target.value })} placeholder="Newsletter" />
          <button type="submit" className="btn btn-secondary" disabled={busy === 'addr' || !addr.value}>{busy === 'addr' ? 'Adding…' : 'Add address'}</button>
          <AddedList items={addresses.map((i) => ({ key: i.id, title: i.display_value, sub: i.label || '' }))} />
        </form>
      </div>
      {footer}
    </>
  );
}

// ---- 4. Website -------------------------------------------------------------------

function WebsiteStep({ org, website, onChanged, footer }) {
  const [busy, setBusy] = useState(null);
  const [results, setResults] = useState({});
  if (!website) return <><p className="muted">Loading…</p>{footer}</>;
  const check = async (d) => {
    setBusy(d.id);
    try {
      const r = await api(`/portal/organisations/${org.id}/domains/${d.id}/check-website`, { method: 'POST' });
      setResults({ ...results, [d.id]: r.found ? { tone: 'success', text: `Found it on ${d.domain}. Your site is protected${r.domain?.status === 'verified' ? ' and the domain is verified' : ''}.` } : { tone: 'warn', text: `Not on ${d.domain} yet (${r.result}). If you’ve just added it, publish your site and check again.` } });
      if (r.found) onChanged();
    } catch (err) { setResults({ ...results, [d.id]: { tone: 'danger', text: err.message } }); }
    setBusy(null);
  };
  const seen = website.domains.some((d) => d.snippet_seen_at);
  return (
    <>
      <p className="setup-lede">One small line of code tells us straight away if a copy of your website appears somewhere else. It also proves your domain, and can show visitors a badge saying they’re on the real site.</p>
      <ol className="setup-howto">
        <li><strong>Copy this code.</strong><CodeBlock code={website.snippets.script} label="Website code" /></li>
        <li><strong>Paste it into every page.</strong> In WordPress, Wix, Squarespace or Webflow, look for “custom code” or “header scripts”. Or send it to whoever runs your site.</li>
        <li><strong>Check it’s working.</strong>
          <div className="setup-check-list">
            {website.domains.map((d) => (
              <div key={d.id} className="setup-check-item">
                <span><Tick done={!!d.snippet_seen_at} /> <span className="mono">{d.domain}</span></span>
                <button type="button" className="btn btn-small btn-secondary" onClick={() => check(d)} disabled={busy === d.id}>{busy === d.id ? 'Checking…' : 'Check my site'}</button>
                {results[d.id] && <Alert tone={results[d.id].tone}>{results[d.id].text}</Alert>}
              </div>
            ))}
          </div>
        </li>
      </ol>
      {seen && <p className="muted small">Want the badge too? You’ll find it on the Website page later.</p>}
      {footer}
    </>
  );
}

// ---- 5 & 6. Phone numbers and bank details --------------------------------------------

function IdentifierStep({ org, identifiers, onChanged, footer, type }) {
  const phone = type === 'phone';
  const [value, setValue] = useState('');
  const [label, setLabel] = useState('');
  const [listed, setListed] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const add = async (e) => {
    e.preventDefault(); setBusy(true); setError('');
    try {
      await api(`/portal/organisations/${org.id}/entities/${org.entity_id}/identifiers`, {
        method: 'POST', body: { type, value, label: label || (phone ? 'Office' : 'Company account'), visibility: phone && listed ? 'public' : 'confirm_only' },
      });
      setValue(''); setLabel(''); onChanged();
    } catch (err) { setError(err.data?.fields?.value || err.message); }
    setBusy(false);
  };
  const mine = identifiers.filter((i) => i.type === type);
  return (
    <>
      {phone ? (
        <p className="setup-lede">Some of the most convincing scams come by phone. List the numbers your team really uses, so anyone who gets a call can check it in seconds.</p>
      ) : (
        <>
          <p className="setup-lede">The costliest event scam is a copy of your invoice with different bank details. Tell us the accounts you invoice from, and any other account is flagged straight away.</p>
          <div className="setup-safe">
            <span className="setup-safe-icon"><Icon name="lock" size={22} /></span>
            <div><strong>Your bank details are never stored in readable form.</strong> We keep a one-way fingerprint and the last four digits, which is enough to recognise the account but can’t be turned back into it. They’re never shown to anyone outside your company.</div>
          </div>
        </>
      )}
      <form className="setup-card setup-narrow" onSubmit={add}>
        <Field label={phone ? 'Phone number' : 'Account'} value={value} onChange={(e) => { setValue(e.target.value); setError(''); }} error={error}
          placeholder={phone ? '+44 20 7946 0000' : '12-34-56 12345678, or an IBAN'} inputMode={phone ? 'tel' : 'text'} autoComplete="off" />
        <Field label={phone ? 'Whose number is it?' : 'Name for it'} optional value={label} onChange={(e) => setLabel(e.target.value)} placeholder={phone ? 'Office, sales, accounts…' : 'Main business account'} />
        {phone && <label className="setup-check"><input type="checkbox" checked={listed} onChange={(e) => setListed(e.target.checked)} /> Show it on our check page, so people can see it’s ours</label>}
        <button type="submit" className="btn btn-primary" disabled={busy || !value.trim()}>{busy ? 'Adding…' : mine.length ? 'Add another' : 'Add'}</button>
        <AddedList items={mine.map((i) => ({ key: i.id, title: i.display_value, sub: i.label || '' }))} />
      </form>
      {footer}
    </>
  );
}

// ---- 7. Suppliers -------------------------------------------------------------------

const BLANK_SUPPLIER = { name: '', category: '', website: '', email: '', phone: '' };

function SuppliersStep({ org, suppliers, categories, onChanged, footer }) {
  const [s, setS] = useState(BLANK_SUPPLIER);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => { setS({ ...s, [k]: e.target.value }); setErrors({}); };
  const add = async (e) => {
    e.preventDefault(); setBusy(true); setErrors({});
    try { await api(`/portal/organisations/${org.id}/suppliers`, { method: 'POST', body: s }); setS(BLANK_SUPPLIER); onChanged(); }
    catch (err) { setErrors(err.data?.fields || { name: err.message }); }
    setBusy(false);
  };
  return (
    <>
      <p className="setup-lede">Your official suppliers, like your housing bureau, stand builder or directory publisher, are exactly who scammers pretend to be. List them so their genuine emails aren’t doubted and fake “official partners” are caught.</p>
      <form className="setup-card setup-narrow" onSubmit={add}>
        <Field label="Supplier name" value={s.name} onChange={set('name')} error={errors.name} placeholder="Harbour Housing Ltd" />
        <Field label="What they do" as="select" value={s.category} onChange={set('category')} error={errors.category}>
          <option value="">Choose…</option>
          {Object.entries(categories || {}).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </Field>
        <Field label="Their website" optional value={s.website} onChange={set('website')} error={errors.website} placeholder="harbourhousing.co.uk" autoCapitalize="none" />
        <div className="setup-grid-2">
          <Field label="Email" optional type="email" value={s.email} onChange={set('email')} error={errors.email} autoCapitalize="none" />
          <Field label="Phone" optional value={s.phone} onChange={set('phone')} error={errors.phone} inputMode="tel" />
        </div>
        <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Adding…' : 'Add and add another'}</button>
        <AddedList items={suppliers.map((x) => ({ key: x.id, title: x.name, sub: x.category_label }))} />
      </form>
      {footer}
    </>
  );
}

// ---- The guide ---------------------------------------------------------------------

export default function Setup() {
  const { organisation, user, refresh } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(() => {
    const asked = STEPS.findIndex((x) => x.key === new URLSearchParams(window.location.search).get('step'));
    if (asked >= 0) return asked;
    try { const raw = organisation ? localStorage.getItem(STORE(organisation.id)) : null; const v = raw === null ? NaN : Number(raw); return Number.isInteger(v) && v >= -1 && v <= STEPS.length ? v : -1; } catch { return -1; }
  });
  const [summary, setSummary] = useState(null);
  const [people, setPeople] = useState([]);
  const [suppliers, setSuppliers] = useState({ suppliers: [], categories: {} });
  const [website, setWebsite] = useState(null);
  const [leaving, setLeaving] = useState(false);

  const base = organisation ? `/portal/organisations/${organisation.id}` : '';
  const load = useCallback(async () => {
    if (!organisation) return;
    const d = await loadSetupData(organisation.id);
    setSummary(d.summary); setPeople(d.people); setSuppliers(d.suppliers); setWebsite(d.website);
  }, [organisation]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { try { if (organisation) localStorage.setItem(STORE(organisation.id), String(step)); } catch { /* private mode */ } }, [step, organisation]);
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, [step]);

  const identifiers = summary?.identifiers || [];
  const status = useMemo(() => setupStatus({ summary, people, website, suppliers, user }), [summary, people, website, suppliers, user]);

  if (!organisation) return <Navigate to="/login" replace />;
  if (!organisation.onboarded) return <Navigate to="/onboarding" replace />;
  if (!['owner', 'admin'].includes(organisation.role || organisation.my_role)) return <Navigate to="/dashboard" replace />;

  const finish = async (skipped) => {
    setLeaving(true);
    try { await api(`${base}/setup/complete`, { method: 'POST', body: { skipped } }); } catch { /* still let them go */ }
    try { localStorage.removeItem(STORE(organisation.id)); } catch { /* ignore */ }
    await refresh();
    navigate('/dashboard', { replace: true });
  };

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length));
  const back = () => setStep((s) => Math.max(s - 1, -1));
  const doneCount = STEPS.filter((s) => status[s.key]).length;
  const current = STEPS[step];
  const footer = current ? <StepFooter onNext={next} onBack={step > 0 ? back : null} done={status[current.key]} nextLabel={step === STEPS.length - 1 ? 'Finish' : undefined} /> : null;
  const props = { org: { ...organisation, entity_id: summary?.organisation?.entity_id }, summary: summary || {}, onChanged: load, footer };

  return (
    <div className="setup-shell">
      <aside className="setup-rail" aria-label="Setup steps">
        <img src="/logo-on-dark.png" alt="SummitShield" width="140" height="34" className="setup-logo" />
        <p className="setup-rail-title">Let’s get you set up</p>
        <div className="setup-progress" role="progressbar" aria-valuemin={0} aria-valuemax={STEPS.length} aria-valuenow={doneCount} aria-label={`${doneCount} of ${STEPS.length} done`}>
          <span style={{ width: `${(doneCount / STEPS.length) * 100}%` }} />
        </div>
        <p className="setup-rail-count">{doneCount} of {STEPS.length} done</p>
        <ol className="setup-steps">
          {STEPS.map((s, i) => (
            <li key={s.key}>
              <button type="button" className={`setup-step${i === step ? ' is-current' : ''}${status[s.key] ? ' is-done' : ''}`} onClick={() => setStep(i)} aria-current={i === step ? 'step' : undefined}>
                <span className="setup-step-num">{status[s.key] ? '✓' : i + 1}</span>
                <span><strong>{s.short}</strong><span>{s.time}</span></span>
              </button>
            </li>
          ))}
        </ol>
        <button type="button" className="setup-leave" onClick={() => finish(true)} disabled={leaving}>Skip setup for now</button>
      </aside>

      <main className="setup-main">
        <div className="setup-mobile-bar">
          <span>{step >= 0 && step < STEPS.length ? `Step ${step + 1} of ${STEPS.length}` : 'Setup'}</span>
          <button type="button" className="link-button" onClick={() => finish(true)} disabled={leaving}>Skip setup</button>
        </div>
        <div className="setup-content" key={step}>
          {!summary ? <p className="muted">Loading…</p> : step === -1 ? (
            <div className="setup-welcome">
              <span className="setup-welcome-badge"><Icon name="wave" size={34} /></span>
              <h1>Welcome{user?.first_name ? `, ${user.first_name}` : ''}. Let’s get you protected.</h1>
              <p className="setup-lede">In about 15 minutes we’ll set up everything that helps your exhibitors and attendees tell your real emails, calls and invoices from the fakes. Skip anything you’d rather do later: it’s all on your Company page too.</p>
              <ol className="setup-preview">{STEPS.map((s, i) => <li key={s.key}><span>{i + 1}</span>{s.short}</li>)}</ol>
              <div className="setup-inline">
                <button type="button" className="btn btn-primary btn-large" onClick={() => setStep(0)}>Let’s go</button>
                <button type="button" className="link-button" onClick={() => finish(true)} disabled={leaving}>I’ll do this later</button>
              </div>
            </div>
          ) : step >= STEPS.length ? (
            <div className="setup-welcome">
              <span className="setup-welcome-badge"><Icon name="star" size={34} /></span>
              <h1>{doneCount === STEPS.length ? 'You’re all set up' : 'Nicely done'}</h1>
              <p className="setup-lede">{doneCount} of {STEPS.length} steps are done. Anything you skipped is waiting on your Company page and your protection plan, whenever you’re ready.</p>
              <ul className="setup-summary">
                {STEPS.map((s, i) => (
                  <li key={s.key} className={status[s.key] ? 'is-done' : ''}>
                    <Tick done={status[s.key]} /><span>{s.short}</span>
                    {!status[s.key] && <button type="button" className="link-button" onClick={() => setStep(i)}>Do it now</button>}
                  </li>
                ))}
              </ul>
              <button type="button" className="btn btn-primary btn-large" onClick={() => finish(false)} disabled={leaving}>Go to my dashboard</button>
            </div>
          ) : (
            <>
              <p className="setup-kicker">Step {step + 1} of {STEPS.length}{status[current.key] ? ' · done' : ''}</p>
              <h1>{{
                domain: 'First, let’s prove your domain is yours',
                email: 'Now, let’s confirm your business email',
                people: 'Next, add your people and sending addresses',
                website: 'Now let’s protect your website',
                phones: 'Add your phone numbers',
                bank: 'Do you want to add your bank details?',
                suppliers: 'Any suppliers you want to add now?',
              }[current.key]}</h1>
              {current.key === 'domain' && <DomainStep {...props} goWebsite={() => setStep(3)} />}
              {current.key === 'email' && <EmailStep {...props} />}
              {current.key === 'people' && <PeopleStep {...props} people={people} identifiers={identifiers} />}
              {current.key === 'website' && <WebsiteStep {...props} website={website} />}
              {current.key === 'phones' && <IdentifierStep {...props} identifiers={identifiers} type="phone" key="phone" />}
              {current.key === 'bank' && <IdentifierStep {...props} identifiers={identifiers} type="bank" key="bank" />}
              {current.key === 'suppliers' && <SuppliersStep {...props} suppliers={suppliers.suppliers || []} categories={suppliers.categories} />}
            </>
          )}
        </div>
        <p className="setup-help">Stuck? <Link to="/help">Help centre</Link> · <Link to="/contact">Contact us</Link></p>
      </main>
    </div>
  );
}
